import {
  CloudAccountApiDeleteCloudAccountByUuidRequest,
  CloudAccountApiDiscoverInventoryRequest,
  CloudAccountApiGetCloudAccountByUuidRequest,
  CloudAccountApiUpdateCloudAccountRequest,
  CloudAccountGetResponse,
  CodeRepositoryApiOnboardCodeRepositoryRequest,
} from '@ariksa/cloud-account/api';
import {
  AlertWorkflowApiGetWorkflowsByClientRequest,
  AlertWorkflowResponse,
  ClientConfig,
  ClientResponse,
  ClientsApiAddClientRequest,
  ClientsApiDeleteClientConfigRequest,
  ClientsApiEditClientRequest,
  ClientsApiGetClientByIdRequest,
  ClientsApiGetClientDetailsRequest,
  ClientsApiGetJiraProjectRequest,
  ClientsApiGetJiraProjectsRequest,
  ClientsApiValidateClientConfigRequest,
  JiraProjects,
} from '@ariksa/notification/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, select, takeLatest } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import {
  AlertVaultClientService,
  AlertVaultWorkflowService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import { AlertVaultJiraService, isJiraClient } from 'api/alert-vault/jira';
import {
  IdentityProviderNameService,
  IdentityProviderService,
} from 'api/auth/api.pb';
import {
  CloudAccountService,
  NotificationService,
  serviceClient,
} from 'api/services';
import { selectUser } from 'containers/App/selectors';

import { callApiFn } from '../../../../utils/saga';

import { actions } from './slice';
import { CreateProviderProps, DeleteProviderProps } from './types';

export function* integrationsSaga() {
  yield takeLatestAction(actions.getAllClients, doGetAllClients);

  yield takeLatest(actions.loadClients.type, doGetClients);
  yield takeLatestAction(actions.deleteClient, doDeleteClient);
  yield takeLatestAction(actions.createClient, doCreateClient);
  yield takeLatestAction(actions.getJiraProjects, doGetJiraProjectDetails);
  yield takeLatestAction(
    actions.getJiraProjectDetails,
    doGetJiraProjectDetails,
  );
  yield takeLatestAction(
    actions.getJiraProjectDetailsForAClient,
    doGetJiraProjectDetailsForAClient,
  );
  yield takeLatestAction(actions.getClientDetailsById, doGetClientDetailsById);
  yield takeLatestAction(actions.updateClientById, doUpdateClient);
  yield takeLatestAction(actions.validateClient, doValidateClient);
  yield takeLatestAction(
    actions.getAlertWorkflowAttachedToClient,
    doGetAlertWorkflowsAttachedToClient,
  );

  // SSO
  yield takeLatest(actions.getProviderNames.type, doGetProviderNames);
  yield takeLatest(actions.getSSOProviders.type, doGetSSOProviders);
  yield takeLatest(actions.createSSOProvider.type, doCreateSSOProvider);
  yield takeLatest(actions.deleteSSOProvider.type, doDeleteSSOProvider);

  //Identity And Access Providers and snowflake
  yield takeLatestAction(actions.getAccountByID, doGetAccountByID);
  yield takeLatestAction(actions.updateAccount, doUpdateAccount);
  yield takeLatestAction(actions.deleteAccount, doDeleteAccount);
  yield takeLatestAction(actions.rediscoverInventory, doRediscoverInventory);
}

export function* doDeleteSSOProvider(
  action: PayloadAction<DeleteProviderProps>,
) {
  const { onSuccess, ...rest } = action.payload;

  yield call(callApiFn, {
    fn: IdentityProviderService.Delete,
    data: rest,
    onSuccess: actions.deleteSSOProviderSuccess,
    onError: actions.deleteSSOProviderError,
    errorTitle: 'Failed to create sso providers',
    onResponse: (res, err) => {
      if (!err) {
        onSuccess();
      }
      return err ? err : res;
    },
  });
}

export function* doCreateSSOProvider(
  action: PayloadAction<CreateProviderProps>,
) {
  const { onRedirect, ...rest } = action.payload;

  yield call(callApiFn, {
    fn: IdentityProviderService.Create,
    data: rest,
    onSuccess: actions.createSSOProviderSuccess,
    onError: actions.createSSOProviderError,
    errorTitle: 'Failed to create sso providers',
    onResponse: (res, err) => {
      if (!err) {
        onRedirect();
      }
      return err ? err : res;
    },
  });
}

export function* doGetSSOProviders() {
  const { info } = yield select(selectUser);

  yield call(callApiFn, {
    fn: IdentityProviderService.List,
    data: { organization: info.organization },
    onSuccess: actions.getSSOProvidersSuccess,
    onError: actions.getSSOProvidersError,
    errorTitle: 'Failed to get sso providers',
  });
}

export function* doGetProviderNames() {
  const { info } = yield select(selectUser);

  yield call(callApiFn, {
    fn: IdentityProviderNameService.List,
    data: { organization: info.organization },
    onSuccess: actions.getProviderNamesSuccess,
    onError: actions.getProviderNamesError,
    errorTitle: 'Failed to get sso provider names',
  });
}

export function* doGetClients(
  action: PayloadAction<ClientsApiGetClientDetailsRequest>,
) {
  yield call(serviceClient, {
    fn: async () =>
      isAlertVaultEnabled()
        ? AlertVaultClientService.list(action.payload.clientName)
        : NotificationService.Clients.getClientDetails(action.payload),
    onSuccess: actions.clientsLoaded,
    onError: actions.clientsLoadingError,
    mapResponse: res => ({
      clientName: action.payload.clientName,
      data: res?.data,
    }),
    errorTitle: 'Failed to get clients!',
  });
}

/*create client*/
function* doCreateClient(ctx: QueryContext<any, ClientsApiAddClientRequest>) {
  // Alert Vault Jira clients are validated and their token stored encrypted.
  const fetcher = () =>
    isAlertVaultEnabled() && isJiraClient(ctx.params)
      ? AlertVaultJiraService.createClient(ctx.params)
      : NotificationService.Clients.addClient(ctx.params);
  yield call(ctx.fetch, fetcher);
}

/*Update Client Details by ID*/
function* doUpdateClient(
  ctx: QueryContext<ClientResponse, ClientsApiEditClientRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled() && isJiraClient(ctx.params)
      ? AlertVaultJiraService.updateClient(ctx.params)
      : NotificationService.Clients.editClient(ctx.params),
  );
}

/*delete client*/
export function* doDeleteClient(
  ctx: QueryContext<any, ClientsApiDeleteClientConfigRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultClientService.delete(ctx.params.uuid)
      : NotificationService.Clients.deleteClientConfig(ctx.params),
  );
}

/*Get client details by ID*/
function* doGetClientDetailsById(
  ctx: QueryContext<ClientResponse, ClientsApiGetClientByIdRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultClientService.get(ctx.params.uuid)
      : NotificationService.Clients.getClientById(ctx.params),
  );
}

/*Get jira projects and users*/
function* doGetJiraProjectDetails(
  ctx: QueryContext<JiraProjects, ClientsApiGetJiraProjectsRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultJiraService.projectsForCredentials(ctx.params)
      : NotificationService.Clients.getJiraProjects(ctx.params),
  );
}

/*Get jira projects and users*/
function* doGetJiraProjectDetailsForAClient(
  ctx: QueryContext<JiraProjects, ClientsApiGetJiraProjectRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultJiraService.clientProjects(ctx.params)
      : NotificationService.Clients.getJiraProject(ctx.params),
  );
}

/*update account*/
function* doUpdateAccount(
  ctx: QueryContext<any, CloudAccountApiUpdateCloudAccountRequest>,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CloudAccount.updateCloudAccount(ctx.params),
    { errorMsg: 'Failed to update account details!' },
  );
}

/*delete account*/
function* doDeleteAccount(
  ctx: QueryContext<any, CloudAccountApiDeleteCloudAccountByUuidRequest>,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CloudAccount.deleteCloudAccountByUuid(ctx.params),
    { errorMsg: 'Failed to delete account!' },
  );
}

/*get accounts by id*/
function* doGetAccountByID(
  ctx: QueryContext<
    CloudAccountGetResponse,
    CloudAccountApiGetCloudAccountByUuidRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CloudAccount.getCloudAccountByUuid(ctx.params),
    { errorMsg: 'Failed to get account details!' },
  );
}

export function* doRediscoverInventory(
  ctx: QueryContext<any, CloudAccountApiDiscoverInventoryRequest>,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CloudAccount.discoverInventory(ctx.params),
    {
      errorMsg: 'Request failed to do rediscovery!',
      successMsg: 'Rediscovery is in progress!',
    },
  );
}

export function* doGetAlertWorkflowsAttachedToClient(
  ctx: QueryContext<
    AlertWorkflowResponse[],
    AlertWorkflowApiGetWorkflowsByClientRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultWorkflowService.list(false, ctx.params.clientId)
        : NotificationService.AlertWorkflow.getWorkflowsByClient(ctx.params),
    {
      errorMsg: 'Failed to get alert workflows attached to client!',
    },
  );
}

export function* doOnboardCodeRepository(
  ctx: QueryContext<any, CodeRepositoryApiOnboardCodeRepositoryRequest>,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CodeRepository.onboardCodeRepository(ctx.params),
    {
      errorMsg: 'Failed to onboard code repository!',
      successMsg: 'Code repository onboarded successfully.',
    },
  );
}

export function* doValidateClient(
  ctx: QueryContext<ClientConfig, ClientsApiValidateClientConfigRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() && isJiraClient(ctx.params)
        ? AlertVaultJiraService.validate(ctx.params)
        : NotificationService.Clients.validateClientConfig(ctx.params),
    {
      errorMsg: 'Failed to validate client!',
    },
  );
}

export function* doGetAllClients(
  ctx: QueryContext<ClientResponse[], ClientsApiGetClientDetailsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultClientService.list(ctx.params.clientName)
        : NotificationService.Clients.getClientDetails(ctx.params),
    {
      errorMsg: 'Failed to get clients!',
    },
  );
}
